import { useState, type ChangeEvent, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button } from "../../common/Button";
import api from "../../../utils/api";
import { parseApiError } from "../../../utils/parseApiError";
import type { ForgotPasswordPhase } from "./types";

export const ForgotPasswordPage = () => {
  const { t, i18n } = useTranslation();

  const [email, setEmail] = useState("");
  const [phase, setPhase] = useState<ForgotPasswordPhase>({ phase: "form" });

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setPhase({ phase: "submitting" });

    try {
      const language = i18n.language?.startsWith("de") ? "de" : "en";
      await api.forgotPassword({ email: email.trim(), language });
      setPhase({ phase: "sent" });
    } catch (error) {
      const { status } = parseApiError({ error });
      toast.error(
        status === 429 ? t("auth.too_many_requests") : t("auth.request_failed"),
      );
      setPhase({ phase: "form" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center bg-gray-50 py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img
          src="/monco-logo-black.png"
          alt="Monco.ai"
          className="mx-auto h-12 w-auto"
        />
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {t("auth.forgot.title")}
        </h2>
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {phase.phase === "sent" ? (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
                {t("auth.forgot.sent")}
              </div>
              <Link
                to="/login"
                className="block text-center text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                {t("auth.back_to_login")}
              </Link>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <p className="text-sm text-gray-600">
                {t("auth.forgot.description")}
              </p>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("auth.forgot.email_label")}
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    disabled={phase.phase === "submitting"}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={email}
                    onChange={handleEmailChange}
                  />
                </div>
              </div>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={phase.phase === "submitting"}
                className="w-full"
              >
                {phase.phase === "submitting"
                  ? t("auth.forgot.submitting")
                  : t("auth.forgot.submit")}
              </Button>
              <Link
                to="/login"
                className="block text-center text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                {t("auth.back_to_login")}
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
